import React, { useState } from 'react';
import { Plus, Stethoscope } from 'lucide-react';
import { BreadAnalysis } from '../../types/analysis';
import { AnalysisDetailsModal } from './AnalysisDetailsModal';

interface AnalysisListProps {
  analyses: BreadAnalysis[];
  onAnalysisDeleted?: () => void;
  onKnowledgeClick?: () => void;
  onCreateAnalysis: () => void;
}

export function AnalysisList({ analyses, onAnalysisDeleted, onKnowledgeClick, onCreateAnalysis }: AnalysisListProps) {
  const [selectedAnalysis, setSelectedAnalysis] = useState<BreadAnalysis | null>(null);

  if (analyses.length === 0) {
    return (
      <div className="h-[500px] bg-black/10 border border-dashed border-white p-8 text-center flex flex-col justify-center items-center">
        <Stethoscope className="w-12 h-12 text-white mx-auto mb-4" strokeWidth={1}/>
        <h3 className="text-lg font-medium mb-2 text-white">No Analyses Yet</h3>
        <p className="text-white/70 mb-6">
          Start by analyzing your first bread to get detailed feedback and improvements.
        </p>
        <button
          onClick={onCreateAnalysis}
          className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500/90 to-amber-600/90 hover:from-amber-400 hover:to-amber-600 text-white rounded-xl transition-all duration-300"
        >
          <Plus className="w-5 h-5" />
          CREATE YOUR FIRST ANALYSIS
        </button>
      </div>
    );
  }

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <>
      <div className="space-y-4">
        {analyses.map(analysis => (
          <div
            key={analysis.id}
            onClick={() => setSelectedAnalysis(analysis)}
            className="bg-gradient-to-br from-[#636363] to-[#D8D8D8] overflow-hidden shadow-md h-[200px] hover:border hover:border-amber-400 hover:shadow-xl transition-shadow duration-200 cursor-pointer relative"
          >
            <div className="flex flex-col sm:flex-row h-full">
              {/* Left side - Analysis info */}
              <div className="p-4 hidden sm:block flex-col justify-between flex-grow sm:w-1/3 bg-opacity-90 shadow-lg">
                <div>
                  <div className="text-xs text-white italic">
                    <span className="text-2xl text-white font-brand font-light italic mb-1">{formatDate(analysis.createdAt)}</span>
                  </div>
                  <div className="bg-gradient-to-br from-[#535353] to-[#7A7979] text-white px-3 py-2 rounded-lg space-y-1 mt-2">
                    <div className="text-sm">
                      <span className="opacity-70">Process:</span> {analysis.parameters.fermentationType}
                    </div>
                    <div className="text-sm">
                      <span className="opacity-70">Duration:</span> {analysis.parameters.fermentationDuration}h
                    </div>
                    <div className="text-sm">
                      <span className="opacity-70">Hydration:</span> {analysis.parameters.hydration}%
                    </div>
                  </div>
                </div>
              </div>

              {/* Right side - Image */}
              <div className="w-full sm:w-2/3 h-full relative flex-shrink-0">
                {analysis.imageUrl && (
                  <img
                    src={analysis.thumbnailUrl || analysis.imageUrl}
                    alt="Bread analysis"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
                <div className="hidden sm:block absolute inset-y-0 left-0 w-[5px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
                <div className="hidden sm:block absolute inset-y-0 left-0 w-[30px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none bg-opacity-50"></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedAnalysis && (
        <AnalysisDetailsModal
          analysis={selectedAnalysis}
          onClose={() => setSelectedAnalysis(null)}
          onDelete={() => {
            if (onAnalysisDeleted) {
              onAnalysisDeleted();
            }
          }}
          onKnowledgeClick={onKnowledgeClick}
        />
      )}
    </>
  );
}